import { useEffect, useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { ActionBar, Button, FieldShell, Tile } from "@fawkes/ui";

// behandeling-config migration follow-on (frontend): the "Medicijn" tab — the MEDICIJN/APOTHEEK-product
// type-specific surface, shown next to "Product" when the product's Type = MEDICIJN or APOTHEEK. It is the
// React replacement for the Jmix MedicijnTabHost iframe (frontend-react/medicijnen ?view=config).
//
// Read: GET producten/{id}/medicijn → { productId, productNaam, version, config{16 fields}, diersoorten }.
// version === null means "no config row yet" (create-on-first-save); the config carries the lazy defaults.
// A non-medication/unknown product 404s.
//
// Write: PUT producten/{id}/medicijn with { version, ...16 fields }. The 16 fields are the tab's full state;
// the server preserves the non-surfaced columns (indicatie, receptplichtig, standaardKeerPerDag, actief).
// On success the local version re-syncs; a 409 surfaces the "gewijzigd door iemand anders" message.
// ROLE_PRODUCT_EDIT is enforced server-side; a 403 surfaces as a save error.
//
// The tab opens DIRECTLY EDITABLE (Jmix parity) with a tab-level Annuleren/Opslaan footer, owning its own
// form state. All urls are RELATIVE (the dataProvider prepends /api once).
//
// Domain option lists (toedieningsvorm, dosis-eenheden, reden, frequentie, gewicht/kuur eenheden) are FIXED
// client-side vocab stored verbatim — faithful to the Jmix ProductConfig.jsx constants; only diersoorten
// comes from the server.

interface Optie {
  id: number;
  label: string;
}

// The 16 editable config fields, faithful to MedicijnConfigResponse.MedicijnConfig.
interface MedicijnConfig {
  diersoortId: number | null;
  toedieningsvorm: string | null;
  vanafGewicht: number | null;
  vanafGewichtEenheid: string | null;
  totGewicht: number | null;
  totGewichtEenheid: string | null;
  toepassenAanwezig: boolean | null;
  toepassenPleeggezin: boolean | null;
  standaardDosering: string | null;
  doseringEenheid: string | null;
  standaardFreqAantal: number | null;
  standaardFreqEenheid: string | null;
  standaardKuurDagen: number | null;
  kuurEenheid: string | null;
  categorie: string | null;
  waarschuwingen: string | null;
}

interface MedicijnResponse {
  productId: number;
  productNaam: string;
  version: number | null;
  config: MedicijnConfig;
  diersoorten: Optie[];
}

// The mutable form mirror: numbers live as strings while typing.
interface MForm {
  diersoortId: string;
  toedieningsvorm: string;
  vanafGewicht: string;
  vanafGewichtEenheid: string;
  totGewicht: string;
  totGewichtEenheid: string;
  toepassenAanwezig: boolean;
  toepassenPleeggezin: boolean;
  standaardDosering: string;
  doseringEenheid: string;
  standaardFreqAantal: string;
  standaardFreqEenheid: string;
  standaardKuurDagen: string;
  kuurEenheid: string;
  categorie: string;
  waarschuwingen: string;
}

// Fixed client-side vocab (faithful to frontend-react/medicijnen/src/ProductConfig.jsx + freq.js). Stored
// verbatim, so option value === label (domain data, not translated).
const VORM_OPTIES = [
  "Tablet", "Capsule", "Drank / vloeibaar", "Injectie", "Druppels",
  "Zalf / crème", "Spot-on", "Poeder", "Pasta", "Inhalatie",
];
const DOSIS_EENHEDEN = ["mg", "ml", "mg/kg", "ml/kg", "µg", "g", "tablet", "druppel", "IE"];
const GEWICHT_EENHEDEN = ["kg", "g"];
const KUUR_EENHEDEN = ["dagen", "weken", "maanden"];
const FREQ_UNITS = ["dag", "week", "maand", "uur"] as const;
const REDEN_OPTIES = [
  "Antibiotica", "Pijnstilling", "Ontsteking", "Antiparasitair", "Maagbescherming",
  "Hart- en vaatziekten", "Huidaandoening", "Oogaandoening", "Ooraandoening",
  "Schildklier", "Epilepsie", "Gedrag", "Supplement", "Overig",
];

function toForm(c: MedicijnConfig): MForm {
  const numStr = (n: number | null) => (n == null ? "" : String(n));
  return {
    diersoortId: numStr(c.diersoortId),
    toedieningsvorm: c.toedieningsvorm ?? "",
    vanafGewicht: numStr(c.vanafGewicht),
    vanafGewichtEenheid: c.vanafGewichtEenheid ?? "kg",
    totGewicht: numStr(c.totGewicht),
    totGewichtEenheid: c.totGewichtEenheid ?? "kg",
    toepassenAanwezig: c.toepassenAanwezig ?? true,
    toepassenPleeggezin: c.toepassenPleeggezin ?? true,
    standaardDosering: c.standaardDosering ?? "",
    doseringEenheid: c.doseringEenheid ?? "mg",
    // Source parity: a NULL frequency-count opens as "1" (the source DEFAULT), not an empty field.
    standaardFreqAantal: numStr(c.standaardFreqAantal ?? 1),
    standaardFreqEenheid: c.standaardFreqEenheid ?? "dag",
    standaardKuurDagen: numStr(c.standaardKuurDagen),
    kuurEenheid: c.kuurEenheid ?? "dagen",
    categorie: c.categorie ?? "",
    waarschuwingen: c.waarschuwingen ?? "",
  };
}

export function MedicijnTab({ id }: { id: string }) {
  const t = useTranslate();

  const { data, isLoading, isError } = useCustom<MedicijnResponse>({
    url: `producten/${id}/medicijn`,
    method: "get",
  });
  const { mutate, isLoading: saving } = useCustomMutation();

  const [form, setForm] = useState<MForm | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [notice, setNotice] = useState<"saved" | "error" | "conflict" | null>(null);

  const payload = data?.data;

  useEffect(() => {
    if (payload) {
      setVersion(payload.version ?? null);
      setForm(toForm(payload.config));
    }
  }, [payload?.productId, payload?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !payload) {
    return <p className="fawkes-error">{t("medicijnTab.loadFailed")}</p>;
  }

  const diersoorten = payload.diersoorten ?? [];

  const cancelEdit = () => {
    setForm(toForm(payload.config));
    setNotice(null);
  };
  const set = <K extends keyof MForm>(key: K, value: MForm[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setNotice(null);
  };

  const freqHint = (aantalStr: string, unit: string): string => {
    const n = Math.max(1, Number(aantalStr) || 1);
    if (unit === "uur") return t("medicijnTab.freq.omDe", { args: [String(n)] });
    return t(`medicijnTab.freq.per.${unit}`, { args: [String(n)] });
  };

  const save = () => {
    if (!form) return;
    const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));
    const idOrNull = (v: string) => (v === "" ? null : Number(v));
    const trimOrNull = (v: string) => (v.trim() === "" ? null : v);

    mutate(
      {
        url: `producten/${id}/medicijn`,
        method: "put",
        values: {
          version,
          // Toepassing
          diersoortId: idOrNull(form.diersoortId),
          toedieningsvorm: trimOrNull(form.toedieningsvorm),
          vanafGewicht: numOrNull(form.vanafGewicht),
          vanafGewichtEenheid: form.vanafGewichtEenheid,
          totGewicht: numOrNull(form.totGewicht),
          totGewichtEenheid: form.totGewichtEenheid,
          toepassenAanwezig: form.toepassenAanwezig,
          toepassenPleeggezin: form.toepassenPleeggezin,
          // Dosering
          standaardDosering: trimOrNull(form.standaardDosering),
          doseringEenheid: form.doseringEenheid,
          standaardFreqAantal: numOrNull(form.standaardFreqAantal),
          standaardFreqEenheid: form.standaardFreqEenheid,
          standaardKuurDagen: numOrNull(form.standaardKuurDagen),
          kuurEenheid: form.kuurEenheid,
          categorie: trimOrNull(form.categorie),
          waarschuwingen: trimOrNull(form.waarschuwingen),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const updated = res.data as unknown as MedicijnResponse;
          setVersion(updated?.version ?? null);
          if (updated?.config) setForm(toForm(updated.config));
          setNotice("saved");
        },
        onError: (err) => {
          setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");
        },
      },
    );
  };

  const noticeText =
    notice === "saved"
      ? t("medicijnTab.saved")
      : notice === "conflict"
        ? t("medicijnTab.saveConflict")
        : notice === "error"
          ? t("medicijnTab.saveError")
          : null;

  return (
    <div className="entingen-tab">
      <div className="entingen-grid">
        {/* ── Toepassing ── */}
        <Tile title={t("medicijnTab.section.toepassing")}>
          <div className="entingen-section">
            <FieldShell label={t("medicijnTab.diersoort")}>
              <select
                className="fui-field__input fui-select"
                value={form?.diersoortId ?? ""}
                onChange={(e) => set("diersoortId", e.target.value)}
              >
                <option value="">{t("medicijnTab.diersoort.all")}</option>
                {diersoorten.map((d) => (
                  <option key={d.id} value={String(d.id)}>
                    {d.label}
                  </option>
                ))}
              </select>
            </FieldShell>

            <FieldShell label={t("medicijnTab.toedieningsvorm")}>
              <select
                className="fui-field__input fui-select"
                value={form?.toedieningsvorm ?? ""}
                onChange={(e) => set("toedieningsvorm", e.target.value)}
              >
                <option value="">{t("medicijnTab.toedieningsvorm.select")}</option>
                {VORM_OPTIES.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </FieldShell>

            <FieldShell label={t("medicijnTab.vanafGewicht")}>
              <div className="entingen-waarde-eenheid">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className="fui-field__input"
                  value={form?.vanafGewicht ?? ""}
                  onChange={(e) => set("vanafGewicht", e.target.value)}
                />
                <select
                  className="fui-field__input fui-select"
                  value={form?.vanafGewichtEenheid ?? "kg"}
                  onChange={(e) => set("vanafGewichtEenheid", e.target.value)}
                >
                  {GEWICHT_EENHEDEN.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </FieldShell>

            <FieldShell label={t("medicijnTab.totGewicht")}>
              <div className="entingen-waarde-eenheid">
                <input
                  type="number"
                  min="0"
                  step="0.1"
                  className="fui-field__input"
                  placeholder="—"
                  value={form?.totGewicht ?? ""}
                  onChange={(e) => set("totGewicht", e.target.value)}
                />
                <select
                  className="fui-field__input fui-select"
                  value={form?.totGewichtEenheid ?? "kg"}
                  onChange={(e) => set("totGewichtEenheid", e.target.value)}
                >
                  {GEWICHT_EENHEDEN.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </FieldShell>
            <p className="fawkes-muted entingen-helper">{t("medicijnTab.gewicht.helper")}</p>

            <fieldset className="entingen-fieldset">
              <legend className="entingen-legend">{t("medicijnTab.toepassenStatus")}</legend>
              <label className="entingen-check">
                <input
                  type="checkbox"
                  checked={form?.toepassenAanwezig ?? false}
                  onChange={(e) => set("toepassenAanwezig", e.target.checked)}
                />
                {t("medicijnTab.toepassenAanwezig")}
              </label>
              <label className="entingen-check">
                <input
                  type="checkbox"
                  checked={form?.toepassenPleeggezin ?? false}
                  onChange={(e) => set("toepassenPleeggezin", e.target.checked)}
                />
                {t("medicijnTab.toepassenPleeggezin")}
              </label>
            </fieldset>
          </div>
        </Tile>

        {/* ── Dosering ── */}
        <Tile title={t("medicijnTab.section.dosering")}>
          <div className="entingen-section">
            {/* Source has Dosering (number) and Eenheid dosering (select) as two labelled fields, not one
                combined field — the "Eenheid dosering" label was missing. */}
            <FieldShell label={t("medicijnTab.dosering")}>
              <input
                type="number"
                min="0"
                step="0.01"
                className="fui-field__input"
                placeholder={t("medicijnTab.dosering.placeholder")}
                value={form?.standaardDosering ?? ""}
                onChange={(e) => set("standaardDosering", e.target.value)}
              />
            </FieldShell>
            <FieldShell label={t("medicijnTab.doseringEenheid")}>
              <select
                className="fui-field__input fui-select"
                value={form?.doseringEenheid ?? "mg"}
                onChange={(e) => set("doseringEenheid", e.target.value)}
              >
                {DOSIS_EENHEDEN.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </FieldShell>

            <FieldShell label={t("medicijnTab.frequentie")}>
              <div className="entingen-waarde-eenheid">
                <input
                  type="number"
                  min="1"
                  className="fui-field__input"
                  value={form?.standaardFreqAantal ?? ""}
                  onChange={(e) => set("standaardFreqAantal", e.target.value)}
                />
                <select
                  className="fui-field__input fui-select"
                  value={form?.standaardFreqEenheid ?? "dag"}
                  onChange={(e) => set("standaardFreqEenheid", e.target.value)}
                >
                  {FREQ_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {t(`medicijnTab.freqUnit.${u}`)}
                    </option>
                  ))}
                </select>
              </div>
            </FieldShell>
            <p className="fawkes-muted entingen-helper">
              = {freqHint(form?.standaardFreqAantal ?? "", form?.standaardFreqEenheid ?? "dag")}
            </p>

            <FieldShell label={t("medicijnTab.kuur")}>
              <div className="entingen-waarde-eenheid">
                <input
                  type="number"
                  min="1"
                  className="fui-field__input"
                  placeholder={t("medicijnTab.kuur.placeholder")}
                  value={form?.standaardKuurDagen ?? ""}
                  onChange={(e) => set("standaardKuurDagen", e.target.value)}
                />
                <select
                  className="fui-field__input fui-select"
                  value={form?.kuurEenheid ?? "dagen"}
                  onChange={(e) => set("kuurEenheid", e.target.value)}
                >
                  {KUUR_EENHEDEN.map((u) => (
                    <option key={u} value={u}>
                      {u}
                    </option>
                  ))}
                </select>
              </div>
            </FieldShell>

            <FieldShell label={t("medicijnTab.reden")}>
              <select
                className="fui-field__input fui-select"
                value={form?.categorie ?? ""}
                onChange={(e) => set("categorie", e.target.value)}
              >
                <option value="">{t("medicijnTab.reden.select")}</option>
                {REDEN_OPTIES.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </FieldShell>

            <FieldShell label={t("medicijnTab.instructie")} span2>
              <textarea
                className="fui-field__input"
                rows={3}
                placeholder={t("medicijnTab.instructie.placeholder")}
                value={form?.waarschuwingen ?? ""}
                onChange={(e) => set("waarschuwingen", e.target.value)}
              />
            </FieldShell>
          </div>
        </Tile>
      </div>

      <ActionBar>
        {noticeText && (
          <span className={`product-notice product-notice--${notice}`} role="status">
            {noticeText}
          </span>
        )}
        <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
          {t("productEditView.cancel")}
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || !form}>
          {t("productEditView.save")}
        </Button>
      </ActionBar>
    </div>
  );
}
